import type { TimeOffEntry, TimeOffStatus, TimeOffType } from '../../../api/timeOff';
import { toLocalAllDayDate } from '../../../utils/timezone';

export interface TimeOffEntryViewModel {
  id: string;
  employeeUpn: string;
  employeeDisplayName: string | null;
  type: TimeOffType;
  status: TimeOffStatus;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  notes: string | null;
  calendarTargetNames: string[];
  needsEmployeeIdentity?: boolean;
}

export interface TimeOffEntryEditDraft {
  employeeUpn: string;
  employeeDisplayName: string | null;
  type: TimeOffType;
  status: TimeOffStatus;
  startLocal: Date;
  endLocal: Date;
  isAllDay: boolean;
  notes: string | null;
  calendarTargetIds: string[];
}

export type TimeOffEntryCreateDraft = TimeOffEntryEditDraft;

export function timeOffEntryToViewModel(entry: TimeOffEntry): TimeOffEntryViewModel {
  return {
    id: entry.id,
    employeeUpn: entry.employeeUpn,
    employeeDisplayName: entry.employeeDisplayName,
    type: entry.type,
    status: entry.status,
    startUtc: entry.startUtc,
    endUtc: entry.endUtc,
    isAllDay: entry.isAllDay,
    notes: entry.notes,
    calendarTargetNames: entry.calendarLinks.map((l) => l.calendarTargetDisplayName),
    needsEmployeeIdentity: entry.needsEmployeeIdentity
  };
}

export function timeOffEntryToEditDraft(entry: TimeOffEntry): TimeOffEntryEditDraft {
  const startLocal = entry.isAllDay ? toLocalAllDayDate(entry.startUtc) : new Date(entry.startUtc);
  const endLocal = entry.isAllDay
    ? (() => {
        const exclusive = toLocalAllDayDate(entry.endUtc);
        const inclusive = new Date(exclusive);
        inclusive.setDate(inclusive.getDate() - 1);
        inclusive.setHours(0, 0, 0, 0);
        return inclusive.getTime() >= startLocal.getTime() ? inclusive : startLocal;
      })()
    : new Date(entry.endUtc);

  return {
    employeeUpn: entry.employeeUpn,
    employeeDisplayName: entry.employeeDisplayName ?? null,
    type: entry.type,
    status: entry.status,
    startLocal,
    endLocal,
    isAllDay: entry.isAllDay,
    notes: entry.notes,
    calendarTargetIds: entry.calendarLinks.map((l) => l.calendarTargetId)
  };
}
